ChecklistEditItem.vue<template>
  <CardSection :loading="isSavableStateLoading" :error="majorError">
    <template #header>
      Checklist Items {{ selectedTemplate ? `- ${selectedTemplate.data.template_name}` : '' }}
    </template>

    <template #body>
      <SubSection sub-section-heading="Manage Checklist Items" :sub-section-nav="subSectionNav">
        <template #body>
          <VeeForm ref="addItemForm" @submit="onAddItem">
            <div class="row">
              <MultiSelectInput v-model="selectedTemplateItem.itemTypeId"
                name="itemType"
                label="Item Type"
                :options="checklistStore.itemTypes"
                label-key="item_type"
                value-key="id"
                rules="required"
                col-style="form-group-standard-column" />
            </div>
            <div class="row">
              <TextArea v-model="selectedTemplateItem.details"
                name="details"
                label="Item Type Details"
                rules="required"
                rows="10"
                cols="100" />
            </div>

            <ActionToolbar :component-state="currentState" 
              :success-message="successMessage" 
              :primary-button-text="selectedTemplateItem.id  ? `Update Item` : `Add Item`"
              :tertiary-enabled="!createNew"
              secondary-button-text="Clear"
              :tertiary-button-text="!selectedTemplateItem.disabled_at ? `Disable` : `Enable` "
              toolbar-size="col-md-6"
              @secondary-click="clearItem"
              @tertiary-click="toggleItemState()"
              :error-message="formError" />  
          </VeeForm>

          <PortalTable ref="template_item_index" table-id="template_item_index"
            mode="local"
            :sort-order="1"
            sort-field="ui_display_order"
            :table-data="records"
            :total-records="records?.count"
            :loading="isSavableStateProcessing"
            empty="No Items found!"
            :filter-values="filterValues"
            filter-display="row"
            @on-row-reorder="onRowReorder"
            @on-filter-change="onFilterChange">
            <template #headers>
              <div class="table-header">
                <span><b>Checklist Items</b></span>
              </div>
              <div v-if="isReorderSuccess">
                <Message closable severity="success" @close="reset">Successfully Reordered!</Message>
              </div>
            </template>
            <template #columns>
              <Column rowReorder headerStyle="width: 2rem" :reorderableColumn="false" />
              <Column field="item_type.item_type" 
                header="Item Type"
                filter-field="item_type" />
              <Column field="item_body" header="Details" body-style="word-break: break-word;" />
              <Column field="created_by.full_name" header="Created By"/>
              <Column field="updated_by.full_name" header="Last Edited By" />
              <Column field="disabled_by.full_name" header="Disabled By" />
              <Column field="state" header="State"
                filter-field="state"
                :show-filter-menu="false"
                :filter-menu-style="{ width: '14rem' }"
                style="min-width: 14rem">
                <template #filter>
                  <MultiSelectFilter ref="state" 
                    filter-field="state"
                    option-value="name"
                    option-label="name"
                    class="form-control form-control-select"
                    :options="stateOptions"
                    @change="onChange" />
                </template>
              </Column>
              <Column header="Actions" class="btn-2-icon-group">
                <template #body="slotProps">
                  <div class="d-flex">
                    <a href="#" title="Edit" class="action btn btn-fill btn-outline-primary mx-2" @click.prevent="updateItem(slotProps.data)">
                      <font-awesome-icon class="desktop-action" :icon="['fas', 'fa-pen-to-square']" /><span class="mobile-action">Edit</span>
                    </a>        
                    <a href="#" title="Delete" class="action btn btn-fill btn-outline-danger" @click.prevent="deleteItem(slotProps.data)">
                      <font-awesome-icon class="desktop-action" :icon="['fas', 'fa-trash']" aria-hidden="true" /><span class="mobile-action">Delete</span>
                    </a>
                  </div>
                </template>
              </Column>
            </template>
          </PortalTable>
        </template>
      </SubSection>
    </template>
  </CardSection>
</template>

<script lang="ts">
import CardSection from "@/src/components/shared/CardSection.vue";
import ActionToolbar from "@/src/components/shared/ActionToolbar.vue";
import SubSection from "@/src/components/shared/SubSection.vue";
import { Form as VeeForm } from 'vee-validate';
import MultiSelectInput from "@/src/components/shared/FormComponents/MultiSelectInput.vue";
import { savableStateMixin } from "@/src/mixins/savableStateMixin";
import {checklistSubSectionNavMixin} from "@/src/components/admin-area/checklist/checklistSubSectionNavMixin";
import TextInput from "@/src/components/shared/FormComponents/TextInput.vue";
import { useChecklistStore } from '@/src/stores/checklist';
import TextArea from "@/src/components/shared/FormComponents/TextArea.vue";
import PortalTable from "@/src/components/shared/PortalTable.vue";
import Column from 'primevue/column';
import Message from 'primevue/message';
import { Item } from "@/src/models/match/checklist/item";
import { FilterMatchMode } from '@primevue/core/api';
import MultiSelectFilter from "@/src/components/shared/FormComponents/MultiSelectFilter.vue";

export default {
  components: {
    CardSection,
    SubSection,
    ActionToolbar,
    VeeForm,
    MultiSelectInput,
    TextInput,
    TextArea,
    PortalTable,
    Column,
    Message,
    MultiSelectFilter
  },
  mixins: [ savableStateMixin, checklistSubSectionNavMixin ],
  setup() {
   const checklistStore = useChecklistStore();

   return { checklistStore };
  },
  data() {
    return {     
      selectedTemplateItem: {
        itemTypeId: null,
        details: null,
        itemType: null,
        checklist_template_id: null,
        id: null,
        disabled_at: null
      },
      records: [],
      isReorderSuccess: null,
      successMessage: null,
      selectedTemplate: null,
      stateOptions: [
        { code: 'enabled', name: "Enabled" },
        { code: 'disabled', name: "Disabled" }
      ],
      filterValues: {
        "state" : { value: null, matchMode: FilterMatchMode.IN }
      },
    }
  },
  computed: {
    createNew() {
      return this.selectedTemplateItem.id == null ? true : false;
    }
  },
  async mounted() {
    await this.getRowData();
  },
  methods: {
    async getRowData() {
      this.records = [];
      try {
        this.setSavableStateProcessing();
        await this.checklistStore.loadItemTypes();
        this.selectedTemplate = await this.checklistStore.loadTemplate(this.$route.params.id);

        const response = await this.checklistStore.loadItems(this.$route.params.id);
        response.forEach((r: any) => {
          this.records.push(Item.buildFromAPI(r))
        });
        
        this.setSavableStateIdle();
      } catch(error) {
        this.handleMajorError(error);
      }
    },
    async onAddItem() {
      try {
        this.setSavableStateProcessing();
        const payload = {
          id: this.selectedTemplateItem.id,
          item_body: this.selectedTemplateItem.details,
          checklist_template_id: this.$route.params.id,
          item_type_id: this.selectedTemplateItem.itemTypeId
        }
        if(this.createNew) { 
          await this.checklistStore.createTemplateItem(payload);
          this.successMessage = 'Successfully created!'
        }
        else {
          await this.checklistStore.updateTemplateItem(payload);
          this.successMessage = 'Successfully updated!'
        }
       
        await this.reload();
        this.setSavableStateSuccess();
      } catch(error) {
        this.handleFormError(error);
      }
    },
    async updateItem(data) {
      this.selectedTemplateItem = {
        id: data.id,
        details: data.item_body,
        itemType: data.item_type.item_type,
        itemTypeId: data.item_type.id,
        checklist_template_id: this.$route.params.id,
        disabled_at: data.disabled_at
      }

      window.scrollTo({
        top: 0,
        behavior: "smooth"
      });
    },
    async clearItem() {
      this.selectedTemplateItem = {
        id: null,
        details: null,
        item_type_id: null,
        disabled_at: null
      }
      if(this.$refs.addItemForm) {
        await this.$refs.addItemForm.resetForm();
      }
    },
    async toggleItemState() {
      try {
        this.setSavableStateProcessing();
        if(!this.selectedTemplateItem.disabled_at) {
          if(!confirm(`Are you sure you want to disable the item (${this.selectedTemplateItem.itemType})?`)) {
            this.setSavableStateIdle();
            return;
          }
          this.selectedTemplateItem = await this.checklistStore.disableItem(this.selectedTemplateItem.id, this.selectedTemplateItem.checklist_template_id);
        } else {
          if(!confirm(`Are you sure you want to enable the item (${this.selectedTemplateItem.itemType})?`)) {
            this.setSavableStateIdle();
            return;
          }
          this.selectedTemplateItem = await this.checklistStore.enableItem(this.selectedTemplateItem.id, this.selectedTemplateItem.checklist_template_id);
        }
               
        await this.reload();
        this.successMessage = 'Successfully updated!';
        this.setSavableStateSuccess();
      } catch(error) {
        this.handleMajorError(error);
      }
    },
    async deleteItem(data) {
      if(!confirm(`Are you sure you want to delete the item (${data.item_type.item_type})?`)) {
        return;
      }
      await this.checklistStore.deleteItem(data.id, data.checklist_template_id);
      this.clearItem();
      this.getRowData();
    },
    async onRowReorder(event) {
      this.records = event.value;

      try {
        this.setSavableStateProcessing();
        const payload = this.records.map((item, index) => ({
          id: item.id,
          ui_display_order: index + 1
        }))

        await this.checklistStore.updateItemOrder(payload, this.$route.params.id);
        this.setSavableStateSuccess();
        this.isReorderSuccess = true;
        this.getRowData();
      } catch(error) {
        this.handleFormError(error);
      }
    },
    async reload() {
      this.selectedTemplateItem = {
        itemTypeId: null,
        details: null,
        itemType: null,
        checklist_template_id: null,
        id: null,
        disabled_at: null
      }
      this.records = [];
      await this.getRowData();

      if(this.$refs.addItemForm) {
        await this.$refs.addItemForm.resetForm();
      }
    },
    reset() {
      this.isReorderSuccess = null;
    },
    onChange(event) {
      this.$refs.template_item_index.resetFilters(event);
    },
     onFilterChange(event) {
      if(event["state"] === "") this.$refs.state.clearFilters();
    }
  }
}
</script>
